<script lang="ts">
  import type { Place, Map } from "../../types";
  import type { Waypoint } from "../../support/connections";
  import { MAP_WIDTH, MAP_HEIGHT } from "../../constants";
  import { getNow } from "../../data/clock";
  import { getFocusBox, getPathways$ } from "../../domain/map";
  import { avoidHazards, trainLocator, type Prediction } from "../../data/location";
  import Hazards from "./hazards.svelte";
  import FeatureOnly from "../feature-only.svelte";
  import MapIcons from "./map-icons.svelte";
  import MapTextbox from "./map-textbox.svelte";
  import MapWaypoints from "./map-waypoints.svelte";
  import UserLocation from "./user-location.svelte";
  import CurrentEvents from "./current-events.svelte";
  import Scroller from "./scroller.svelte";
  import ImageTile from "./image-tile.svelte";
  import GPSButton from "./gps-button.svelte";
  import DemoButton from "./demo-button.svelte";
  import { startGps } from "../../services/geolocation";
  import Sprite from "../sprite.svelte";
  import { writable, type Writable } from "svelte/store";
  import { settings } from "../../data/settings";
  import { get, minBy } from "lodash-es";
  import type { Point } from "../../support/geometry";

  export let map: Map;
  export let places: Place[];

  export let linkBuilder: (query: { [key: string]: string }) => string;
  export let controlsOffset = 0;
  export let positionX = writable(0);
  export let positionY = writable(0);
  export let positionZ = writable(1);
  export let activePlaceId: string | null = null;
  export let activeWaypointId: string | null = null;
  export let showControls = true;
  export let youAreHere: Point | null = null;
  export let initialZoom = 1;
  export let time = getNow();
  export let autofit = false;

  let cameraOverride: Writable<Prediction>;

  $: angle = get(map, "template.rotation", 0);
  $: layers = get(map, "template.layerImages", []);
  $: width = get(map, "template.width", MAP_WIDTH);
  $: height = get(map, "template.height", MAP_HEIGHT);
  $: geopoints = get(map, "template.geopoints", []);
  $: fonts = get(map, "fonts", []);
  $: textboxes = get(map, "textboxes", []);
  $: waypoints = get(map, "waypoints", []);

  $: focus = getFocusBox(map, activePlaceId);
  $: camera = cameraOverride || trainLocator({ angle, geopoints });
  $: navigation = showControls && geopoints.length > 0;

  function setHazardsFilter(event: { detail: boolean }) {
    avoidHazards.set(event.detail);
  }

  $: pathways = getPathways$(camera, waypoints, activePlaceId, activeWaypointId);

  $: pathCandidates = $avoidHazards ? $pathways.withoutHazards : $pathways.withHazards;

  $: route = minBy(pathCandidates, "cost");

  $: hasHazards = route ? route.path.some((i: Waypoint) => i.hazard) : false;
  $: canReroute = minBy($pathways.withoutHazards, "cost");
  $: showWarning = hasHazards || $avoidHazards;

  $: drawerOffset = showWarning ? 40 : 0;
  $: warningOffset = showWarning ? drawerOffset + 8 : 0;
  $: totalControlsOffset = controlsOffset + warningOffset;

  let isTracking = false;

  let zoom = initialZoom;

  function onLocate() {
    startGps();
    isTracking = true;
  }

  function onPan() {
    isTracking = false;
  }

  function onZoom(event: { detail: number }) {
    zoom = event.detail;
  }

  $: isDebug = $settings.showWaypoints == true;

  let frame: HTMLElement;
  let manuallySetLocation = false;

  function updateUserLocation(touchX: number, touchY: number) {
    cameraOverride = cameraOverride || writable();

    let offsetTop = frame.getBoundingClientRect().y;
    let x = ($positionX + touchX) / $positionZ;
    let y = ($positionY + touchY - offsetTop) / $positionZ;

    cameraOverride.set({ x, y, gravity: 20, shape: "circle" });
  }

  function updateUserLocationFromTouch(event: TouchEvent) {
    if (manuallySetLocation || (event.shiftKey && isDebug)) {
      let touchX = get(event, "touches.0.clientX", 0);
      let touchY = get(event, "touches.0.clientY", 0);

      updateUserLocation(touchX, touchY);
    }
  }

  function updateUserLocationFromMouse(event: MouseEvent) {
    if (manuallySetLocation || (event.shiftKey && isDebug)) {
      updateUserLocation(event.clientX, event.clientY);
    }
  }
</script>

<svelte:window
  on:mousemove={updateUserLocationFromMouse}
  on:touchstart={updateUserLocationFromTouch}
/>

<svelte:head>
  {#each fonts as font}
    <link rel="stylesheet" href={font.resourceUrl} />
  {/each}
</svelte:head>

<Scroller
  {positionX}
  {positionY}
  {positionZ}
  {initialZoom}
  {width}
  {height}
  {focus}
  {camera}
  {isTracking}
  {autofit}
  bind:container={frame}
  on:pan={onPan}
  on:zoom={onZoom}
>
  <ImageTile {layers} {width} {height} />

  <MapIcons icons={map.icons} />

  <FeatureOnly flags={["showWaypoints"]}>
    <MapWaypoints {width} {height} {waypoints} />
  </FeatureOnly>

  {#each textboxes as textbox}
    <MapTextbox
      {textbox}
      {map}
      {linkBuilder}
      {activePlaceId}
      {zoom}
      {time}
      place={places.find((p) => p.id == textbox.placeId)}
      stroke={map.textStrokeColor}
    />
  {/each}

  {#if youAreHere}
    <Sprite
      style="position: absolute; top: {youAreHere.y - 118}px; left: {youAreHere.x -
        30}px; filter: drop-shadow(0 0 24px rgba(0, 0, 0, 0.4))"
      fill="none"
      icon="icon-you-are-here"
      width={124}
      height={142}
    />
  {/if}

  <CurrentEvents {route} {waypoints} {activePlaceId} {activeWaypointId} {width} {height} {zoom} />

  {#if navigation}
    {#if $camera}
      <UserLocation
        location={$camera}
        transition={cameraOverride == null}
        fence={{ x: 0, y: 0, width, height }}
        {zoom}
      />
    {/if}
  {/if}
</Scroller>

{#if showControls}
  {#if $settings.manualLocation}
    <DemoButton offset={totalControlsOffset} bind:active={manuallySetLocation} />
  {:else if navigation}
    <GPSButton offset={totalControlsOffset} {isTracking} {onLocate} />
  {/if}

  <Hazards enabled={$avoidHazards} {hasHazards} {canReroute} on:filter={setHazardsFilter} />
{/if}

<div style="--drawer-offset: {drawerOffset}px">
  <slot />
</div>
